import { useId, type ReactNode } from "react";
import { Chart, type ChartData, type ChartSeries } from "../Chart.tsx";
import { defaultNumberFormat, estimateSuffix, type Measure } from "../estimate.ts";

/**
 * KpiCard — the headline metric tile of the observability kit (observability-ui task 1.2; design
 * §Components and Interfaces "KpiCard" and the "Summary tiles" reference pattern: a composite tile
 * with a value + trend).
 *
 * A grid-friendly card (it sits in `DashboardGrid`'s `.obs-grid__kpis` row) showing a label, a big
 * value, an optional delta/trend chip, and an optional inline sparkline. The sparkline is a COMPACT
 * instance of the shared `components/Chart.tsx` uPlot wrapper (task brief) rather than a bespoke
 * drawing, so it inherits the wrapper's theme parity, accessible data-table alternative and honest
 * empty/loading states for free — one charting path across the whole kit.
 *
 * Accessibility (Requirement 1.3; design 12.4):
 * - The card is a labelled region: a `<section>` with an `aria-labelledby` pointing at the visible
 *   label, so assistive tech announces "KPI: <label>" as a landmark and a keyboard user can reach
 *   it. It carries `tabIndex={0}` so the whole tile is focusable/navigable even when it holds no
 *   interactive child.
 * - The trend direction is conveyed by a WORD + glyph (never colour alone): the chip reads e.g.
 *   "▲ up 12%". Colour (via `--status-*`) is only a redundant fourth channel. An unknown/flat trend
 *   is neutral — it is never shown as the healthy green.
 *
 * Data honesty (Requirement 1.2; design Property 2): with `loading` or `empty` the card renders an
 * honest state and never fabricates a value. A `value` is only ever what the caller passes; the
 * card invents nothing.
 *
 * Theme (Requirement 2.1): every colour comes from the theme tokens in `theme.css` via the kit's
 * `observability.css` — there are no hard-coded colours here, so light and dark both work with no
 * code change.
 *
 * Presentational only: props in, no data fetching, no routing.
 */

/** The trend direction a KPI chip conveys — in words, never colour alone. */
export type KpiTrend = "up" | "down" | "flat";

export interface KpiCardProps {
  /** The metric's name, e.g. "Decisions /s". Doubles as the card's accessible region name. */
  readonly label: string;
  /**
   * The headline value. A string is rendered verbatim (the caller formatted it); a number is
   * grouped with {@link defaultNumberFormat} unless `format` is supplied. Never fabricated by the
   * card — pass `empty`/`loading` when there is no backing read.
   */
  readonly value: number | string;
  /**
   * The change versus the previous window, e.g. "+12%" or "-3". Rendered verbatim in the chip
   * alongside the trend word. Omit when there is nothing to compare.
   */
  readonly delta?: string;
  /** The trend direction for the chip's word/glyph/colour. Defaults to `flat` (neutral) when omitted. */
  readonly trend?: KpiTrend;
  /** An optional inline sparkline series, oldest → newest. Drawn with a compact Chart instance. */
  readonly spark?: readonly number[];
  /** A unit suffix shown after the value, e.g. "/s", "ms". */
  readonly unit?: string;
  /** Confidence in the value (design 12.3): an estimate carries " est."; unverifiable is spelled out. */
  readonly measure?: Measure;
  /** Format a numeric `value`/spark point. Defaults to a grouped number. */
  readonly format?: (value: number) => string;
  /** Still fetching: render a polite loading state instead of a value. */
  readonly loading?: boolean;
  /** No backing read: render an honest empty state instead of a value. */
  readonly empty?: boolean;
  /** Override the empty-state hint. Defaults to "No reading available.". */
  readonly emptyHint?: string;
  /**
   * An optional decorative icon shown on the RIGHT of the card header (e.g. a futuristic AI-robot
   * mark for an "Agents" tile). Purely decorative — the label is the accessible name — so it is
   * marked aria-hidden by the kit's wrapper. Pass an inline SVG so it inherits the theme via
   * `currentColor`.
   */
  readonly icon?: ReactNode;
}

/** The glyph paired with each trend so the direction is legible without the colour. */
const TREND_GLYPH: Readonly<Record<KpiTrend, string>> = { up: "▲", down: "▼", flat: "→" };

/** The direction word — the trend in text, so it is never conveyed by colour or glyph alone. */
const TREND_WORD: Readonly<Record<KpiTrend, string>> = { up: "up", down: "down", flat: "flat" };

export function KpiCard(props: KpiCardProps): ReactNode {
  const labelId = useId();
  const format = props.format ?? defaultNumberFormat;
  const trend = props.trend ?? "flat";
  const unit = props.unit ?? "";

  const valueText =
    typeof props.value === "number" ? format(props.value) : props.value;

  return (
    <section
      className="obs-panel obs-kpi"
      aria-labelledby={labelId}
      tabIndex={0}
    >
      <header className="obs-panel__header">
        <h3 id={labelId} className="obs-panel__title obs-kpi__label">
          {props.label}
        </h3>
        {props.icon !== undefined && (
          <span className="obs-kpi__icon" aria-hidden="true">
            {props.icon}
          </span>
        )}
      </header>

      {props.loading ? (
        <div className="obs-state" data-kind="loading" role="status" aria-live="polite">
          <strong>Loading…</strong>
          <span className="obs-state__hint">Fetching {props.label}.</span>
        </div>
      ) : props.empty ? (
        <div className="obs-state" data-kind="empty">
          <strong>No reading</strong>
          <span className="obs-state__hint">{props.emptyHint ?? "No reading available."}</span>
        </div>
      ) : (
        <div className="obs-panel__body obs-kpi__body">
          <p className="obs-kpi__value">
            <span className="obs-kpi__number">{valueText}</span>
            {unit !== "" && <span className="obs-kpi__unit">{unit}</span>}
            {props.measure !== undefined && props.measure !== "exact" && (
              <span className="obs-kpi__measure">{estimateSuffix(props.measure).trim()}</span>
            )}
          </p>

          {props.delta !== undefined && (
            <p className="obs-kpi__chip" data-trend={trend}>
              <span className="obs-kpi__chip-glyph" aria-hidden="true">
                {TREND_GLYPH[trend]}
              </span>
              <span className="obs-kpi__chip-word">{TREND_WORD[trend]}</span>
              <span className="obs-kpi__chip-delta">{props.delta}</span>
            </p>
          )}

          {props.spark !== undefined && props.spark.length > 0 && (
            <div className="obs-kpi__spark">
              <Chart
                title={`${props.label} trend`}
                data={toSparkData(props.spark)}
                series={SPARK_SERIES}
                x="linear"
                height={36}
                formatY={format}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Shape a flat series into uPlot's aligned `[xs, ys]` for the sparkline: a synthetic 0..n-1 x axis
 * (the sparkline cares about shape, not absolute time) and the values as the single y column.
 */
function toSparkData(values: readonly number[]): ChartData {
  const xs = values.map((_, i) => i);
  const ys = values.map((v) => (Number.isFinite(v) ? v : 0));
  return [xs, ys];
}

/** The single accent-stroked series the sparkline draws; theme-resolved by the Chart wrapper. */
const SPARK_SERIES: readonly ChartSeries[] = [{ label: "trend", colorVar: "--accent", width: 1.25 }];
